import { Text } from '@loomhq/lens';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CEE, ENTER, ESCAPE } from 'constants/keyCodes';
import { isMobile } from 'utilities/device';
import { secondsToVideoTS } from 'utilities/time';

import Description, { isEmpty } from './linked-description';
import { updateVideoDescription } from './graphql';

import AutoresizeTextarea from 'components/ui-components/autoresize-textarea';

import styles from './styles.module.less';

const ADD_DESCRIPTION_MESSAGE = 'Add a description...';

const protect = description => description || '';
const isFirstUpdate = storedDescription => storedDescription == null;

const VideoDescription = () => {
  const dispatch = useDispatch();

  const video = useSelector(state => state.share_video.video);
  const isOwner = useSelector(state => state.share_video.current_user_is_owner);
  const currentTime = useSelector(state => state.video_player.current_time);

  const { id, description: storedDescription } = video;
  const ref = useRef(null);
  const [showInput, setShowInput] = useState(false);

  const [description, setDescription] = useState(protect(storedDescription));

  const onSaveDescription = useCallback(
    newDescription => {
      setDescription(newDescription.trim());
      dispatch(
        updateVideoDescription(
          dispatch,
          id,
          newDescription.trim(),
          video.video_properties.duration,
          isFirstUpdate(storedDescription)
        )
      );
      setShowInput(false);
    },
    [dispatch, id, video.video_properties.duration, storedDescription]
  );

  const saveIfEnterOrEscapePressed = e => {
    if (e.keyCode === CEE && e.ctrlKey && e.shiftKey) {
      const hasNoDescription = isEmpty(description);
      const chapter = `${hasNoDescription ? '' : '\n'}${secondsToVideoTS(
        Math.round(currentTime)
      )} - `;

      setDescription(description + chapter);
    }

    if (e.keyCode === ESCAPE) {
      setDescription(storedDescription);
      setShowInput(false);
    } else if (e.keyCode === ENTER && !e.shiftKey) {
      onSaveDescription(description);
    }
  };

  useEffect(() => {
    if (ref && ref.current && showInput) {
      const ele = ref.current;

      // focus the text area
      ele.focus();
      const end = ele.value.length;

      // place cursor at the end
      ele.setSelectionRange(end, end);
    }
  }, [showInput]);

  const canEdit = isOwner && !isMobile;

  return (
    <>
      {showInput && canEdit ? (
        <>
          <AutoresizeTextarea
            textRef={ref}
            className={cx(
              styles.autoHeight,
              styles.resizeVert,
              styles.input,
              styles.hintPadding
            )}
            value={description}
            placeholder={ADD_DESCRIPTION_MESSAGE}
            onChange={e => setDescription(e.target.value)}
            onKeyDown={saveIfEnterOrEscapePressed}
            onBlur={() => onSaveDescription(description)}
          />
          {description.length !== 0 ? (
            <div className={styles.saveHint}>
              <Text className={styles.bold}>Shift-enter</Text>
              <Text>for a new line</Text>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {(description || canEdit) && (
            <Description
              canEdit={canEdit}
              onClick={() => setShowInput(true)}
              defaultText={ADD_DESCRIPTION_MESSAGE}
              description={description}
            />
          )}
        </>
      )}
    </>
  );
};

export default VideoDescription;
